import { Link } from "react-router-dom";
import { ArrowUpRight, Coffee, Clock, MapPin } from "lucide-react";
import { CAFE_INFO, MENU, GALLERY, IMAGES, WHATSAPP_ORDER_URL } from "../data/cafe";

const featured = [
  { cat: "Avil Milk", name: "Rio Nut", price: 120, img: GALLERY[3].url },
  { cat: "Monster", name: "Nutella Monster", price: 180, img: GALLERY[4].url },
  { cat: "Mojito", name: "Passion Chilli", price: 100, img: GALLERY[5].url },
  { cat: "Rolls", name: "Jumbo Chicken Roll", price: 140, img: IMAGES.rollHero, to: "/rolls" },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={IMAGES.storefront}
            alt="mismos cafe storefront"
            className="w-full h-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-[#050505]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(255,140,90,0.15),transparent_60%)]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-20 w-full">
          <div className="max-w-3xl animate-fade-up">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px w-12 bg-[#FF8C5A]" />
              <span className="text-xs tracking-[0.4em] uppercase text-[#FF8C5A]">
                Est. mismos cafe
              </span>
            </div>
            <h1
              data-testid="hero-title"
              className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] font-medium"
            >
              Your Daily
              <br />
              Dose of <span className="text-gradient italic">Chill.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-white/70 max-w-xl leading-relaxed">
              A slow-paced corner for hand-crafted shakes, Arabic-inspired
              indulgences and late-night comfort food. Pull up a chair.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link
                to="/menu"
                data-testid="hero-cta-menu"
                className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-warm-gradient text-black font-medium hover:opacity-90 transition"
              >
                Explore the Menu
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
              </Link>
              <a
                href={WHATSAPP_ORDER_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-cta-order"
                className="inline-flex items-center gap-3 px-7 py-4 rounded-full border border-white/20 hover:border-[#FF8C5A] hover:text-[#FF8C5A] transition"
              >
                Order Now
              </a>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-0 right-0 z-10 hidden md:block">
          <div className="max-w-7xl mx-auto px-10 flex justify-between items-end text-xs uppercase tracking-[0.3em] text-white/40">
            <span>Scroll to explore</span>
            <span>15 categories • rolls + shakes + food</span>
          </div>
        </div>
      </section>

      {/* MARQUEE-LIKE INTRO BAND */}
      <section className="border-y border-white/10 py-10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-wrap items-center justify-around gap-6 text-white/60">
          <div className="font-display italic text-2xl text-white/80">Chilled.</div>
          <div className="h-1 w-1 rounded-full bg-[#FF8C5A]" />
          <div className="font-display italic text-2xl text-white/80">Crafted.</div>
          <div className="h-1 w-1 rounded-full bg-[#FF8C5A]" />
          <div className="font-display italic text-2xl text-white/80">Cozy.</div>
          <div className="h-1 w-1 rounded-full bg-[#FF8C5A]" />
          <div className="font-display italic text-2xl text-white/80">Curated.</div>
          <div className="h-1 w-1 rounded-full bg-[#FF8C5A]" />
          <div className="font-display italic text-2xl text-white/80">Comforting.</div>
        </div>
      </section>

      {/* STORY */}
      <section className="relative py-28 md:py-40" data-testid="home-story">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden">
              <img
                src={GALLERY[1].url}
                alt="mismos interior"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-8 -right-4 md:-right-10 bg-[#0f0f0f] border border-white/10 rounded-2xl p-6 shadow-2xl backdrop-blur">
              <div className="text-4xl font-display text-gradient">150+</div>
              <div className="text-xs uppercase tracking-[0.25em] text-white/60 mt-1">
                Drinks & Bites
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <span className="text-xs uppercase tracking-[0.4em] text-[#FF8C5A]">
              Our Story
            </span>
            <h2 className="font-display text-4xl md:text-6xl mt-5 leading-tight">
              A quiet rebellion against
              <span className="text-gradient italic"> the rush.</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mt-8">
              mismos was born from a simple idea — that the best things happen
              slowly. We pour our shakes a little thicker, layer our faloodas
              a little richer, and toast our buns a touch longer. Because chill
              is not a vibe. It is a craft.
            </p>
            <p className="text-white/60 leading-relaxed mt-4">
              From the signature Avil Milk to the towering Monster Shakes and
              the Arabic-inspired Roob series — every item on our menu earned
              its place.
            </p>
            <Link
              to="/about"
              data-testid="home-story-cta"
              className="inline-flex items-center gap-2 mt-10 text-[#FF8C5A] hover:text-white transition"
            >
              Read our full story <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="py-24 md:py-32 bg-[#0a0a0a]" data-testid="home-featured">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <span className="text-xs uppercase tracking-[0.4em] text-[#FF8C5A]">
                Signatures
              </span>
              <h2 className="font-display text-4xl md:text-5xl mt-4">
                The ones we&apos;re famous for.
              </h2>
            </div>
            <Link
              to="/menu"
              data-testid="featured-view-all"
              className="inline-flex items-center gap-2 text-white/70 hover:text-[#FF8C5A] transition"
            >
              View full menu <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((f, i) => {
              const Wrapper = f.to ? Link : "div";
              const wrapperProps = f.to ? { to: f.to } : {};
              return (
                <Wrapper
                  key={f.name}
                  {...wrapperProps}
                  data-testid={`featured-item-${i}`}
                  className="group relative overflow-hidden rounded-2xl bg-[#111] border border-white/5 hover:border-[#FF8C5A]/40 transition"
                >
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={f.img}
                      alt={f.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                    />
                  </div>
                  <div className="p-5">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF8C5A]">
                      {f.cat}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="font-display text-xl">{f.name}</div>
                      <div className="text-white/70">₹{f.price}</div>
                    </div>
                  </div>
                </Wrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* CATEGORIES STRIP */}
      <section className="py-24 md:py-32" data-testid="home-categories">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs uppercase tracking-[0.4em] text-[#FF8C5A]">
              On the menu
            </span>
            <h2 className="font-display text-4xl md:text-5xl mt-4">
              Fifteen ways to chill.
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {MENU.map((c) => (
              <Link
                to="/menu"
                key={c.category}
                data-testid={`home-cat-${c.category.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-5 py-2.5 rounded-full border border-white/10 text-sm text-white/70 hover:text-black hover:bg-warm-gradient hover:border-transparent transition"
              >
                {c.category}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ROLLS HUB SECTION */}
      <section className="relative py-24 md:py-32 border-y border-white/10 overflow-hidden" data-testid="home-rolls">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <span className="text-xs uppercase tracking-[0.4em] text-[#FF8C5A]">
              mismos Rolls · Juice Hub
            </span>
            <h2 className="font-display text-4xl md:text-6xl mt-4 leading-tight">
              Hand-rolled, <span className="italic text-gradient">fire-fresh.</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mt-6 max-w-lg">
              Right next to the cafe — our Rolls hub. Chicken, veg, spicy,
              cheesy. Paired with cold-pressed juices and dry-fruit shakes.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {["Chicken Rolls", "Veg Rolls", "Fresh Juice", "Dry Fruit Shake"].map((t) => (
                <span
                  key={t}
                  className="px-4 py-2 rounded-full border border-white/15 text-sm text-white/80"
                >
                  {t}
                </span>
              ))}
            </div>
            <Link
              to="/rolls"
              data-testid="home-rolls-cta"
              className="inline-flex items-center gap-3 mt-10 px-7 py-4 rounded-full bg-warm-gradient text-black font-medium hover:opacity-90 transition"
            >
              Explore the Rolls menu <ArrowUpRight size={18} />
            </Link>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2 grid grid-cols-2 gap-4">
            <div className="aspect-[3/4] rounded-2xl overflow-hidden col-span-1">
              <img
                src={IMAGES.rollHero}
                alt="mismos signature chicken roll"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="aspect-[3/4] rounded-2xl overflow-hidden col-span-1 mt-8">
              <img
                src={IMAGES.rollDisplay}
                alt="Fresh roll fillings display"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={GALLERY[2].url}
            alt="cafe vibe"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-[#050505]/70" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display text-4xl md:text-6xl leading-tight">
            Come slow down with us.
          </h2>
          <p className="text-white/70 mt-6 text-lg">
            Book your table, grab your favourite corner, and let the evening
            happen.
          </p>
          <a
            href={WHATSAPP_ORDER_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="home-cta-order"
            className="inline-flex items-center gap-3 mt-10 px-8 py-4 rounded-full bg-warm-gradient text-black font-medium hover:opacity-90 transition"
          >
            Order on WhatsApp <ArrowUpRight size={18} />
          </a>

          <div className="grid sm:grid-cols-3 gap-6 mt-20 text-left">
            <div className="rounded-2xl border border-white/10 p-6 bg-black/40 backdrop-blur">
              <Coffee className="text-[#FF8C5A]" size={20} />
              <div className="font-display text-xl mt-3">Hand-crafted</div>
              <div className="text-white/60 text-sm mt-1">
                Every drink, blended to order.
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 p-6 bg-black/40 backdrop-blur">
              <Clock className="text-[#FF8C5A]" size={20} />
              <div className="font-display text-xl mt-3">Late nights</div>
              <div className="text-white/60 text-sm mt-1">
                Open till midnight on weekends.
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 p-6 bg-black/40 backdrop-blur">
              <MapPin className="text-[#FF8C5A]" size={20} />
              <div className="font-display text-xl mt-3">Neighborhood spot</div>
              <div className="text-white/60 text-sm mt-1">
                {CAFE_INFO.address}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
